import { useEffect, useState } from "react";
import { firebaseIdle } from "@/lib/firebase/app";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";

/**
 * Het Kiyoh-cijfer van de winkel, uit Firestore `settings/kiyoh`.
 *
 * Waarom via Firestore en niet rechtstreeks bij Kiyoh: hun statistieken-API eist een API-sleutel,
 * en die hoort niet in een browserbundel. De nachtelijke Cloud Function `syncKiyohStats` haalt het
 * cijfer op en zet het in dat (publiek leesbare) doc; hier lezen we het met één gecachte read per
 * sessie — hetzelfde patroon als [[src/lib/popularity.ts]].
 *
 * Geen doc / mislukte read (bv. App Check op localhost) → `null`, en dan tonen we nergens een
 * cijfer. Nooit een waardering claimen die we niet kunnen onderbouwen.
 */
/** Eén beoordeling zoals Kiyoh 'm publiceert. */
export type KiyohReview = {
  id: string;
  author: string;
  city: string | null;
  /** Cijfer op 10. */
  rating: number;
  title: string | null;
  text: string;
  /** Onze publieke reactie eronder, als die er is. */
  reply: string | null;
  date: number | null;
};

export type KiyohStats = {
  /** Gemiddelde op Kiyoh's eigen schaal van 10 (zo toont Kiyoh 'm ook). */
  average: number;
  count: number;
  recommend: number | null;
  /** De laatste beoordelingen mét tekst — leeg als er alleen kale cijfers zijn. */
  reviews: KiyohReview[];
};

let cached: KiyohStats | null | undefined;
let pending: Promise<KiyohStats | null> | null = null;
const subscribers = new Set<(s: KiyohStats | null) => void>();

/** Reviews uit het Firestore-doc, met een controle per veld: het doc wordt door een Cloud Function
 *  gevuld met wat Kiyoh teruggaf, en daar willen we hier niet blind op vertrouwen. */
function leesReviews(raw: unknown): KiyohReview[] {
  if (!Array.isArray(raw)) return [];
  const out: KiyohReview[] = [];
  for (const r of raw) {
    const v = r as Record<string, unknown>;
    const text = typeof v.text === "string" ? v.text.trim() : "";
    if (!text) continue;
    const rating = Number(v.rating);
    const date = Number(v.date);
    out.push({
      id: String(v.id ?? text.slice(0, 24)),
      author: typeof v.author === "string" && v.author.trim() ? v.author.trim() : "Klant",
      city: typeof v.city === "string" && v.city.trim() ? v.city.trim() : null,
      rating: Number.isFinite(rating) ? rating : 0,
      title: typeof v.title === "string" && v.title.trim() ? v.title.trim() : null,
      text,
      reply: typeof v.reply === "string" && v.reply.trim() ? v.reply.trim() : null,
      date: Number.isFinite(date) && date > 0 ? date : null,
    });
  }
  return out;
}

async function readDoc(): Promise<KiyohStats | null> {
  try {
    const [{ doc, getDoc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const s = await getDoc(doc(db, "settings", "kiyoh"));
    if (!s.exists()) return null;
    const d = s.data() as { average?: unknown; count?: unknown; recommend?: unknown; reviews?: unknown };
    const average = Number(d.average);
    const count = Number(d.count);
    if (!Number.isFinite(average) || average <= 0 || !Number.isFinite(count) || count < 1) return null;
    const recommend = Number(d.recommend);
    return {
      average,
      count,
      recommend: Number.isFinite(recommend) && recommend > 0 ? recommend : null,
      reviews: leesReviews(d.reviews),
    };
  } catch {
    return null;
  }
}

function loadOnce(): Promise<KiyohStats | null> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (!pending) {
    pending = firebaseIdle().then(readDoc).then((s) => {
      pending = null;
      cached = s;
      subscribers.forEach((fn) => fn(s));
      return s;
    });
  }
  return pending;
}

/** Hook: het winkelcijfer, of `null` zolang er nog geen (gepubliceerde) beoordelingen zijn. */
export function useKiyohStats(): KiyohStats | null {
  const [stats, setStats] = useState<KiyohStats | null>(cached ?? null);
  useEffect(() => {
    if (cached !== undefined) { setStats(cached); return; }
    let alive = true;
    const fn = (s: KiyohStats | null) => { if (alive) setStats(s); };
    subscribers.add(fn);
    void loadOnce();
    return () => { alive = false; subscribers.delete(fn); };
  }, []);
  return stats;
}

/** "9,4" — Kiyoh's schaal van 10, Nederlandse komma. */
export const kiyohScoreLabel = (average: number): string => average.toFixed(1).replace(".", ",");

/** Kiyoh rekent op 10, sterren op 5. */
export const kiyohStars = (average: number): number => average / 2;
